// src/components/PerformanceMap/PerformanceViewModes.tsx
import React, { useState, useRef, useEffect } from 'react';
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  useReactFlow,
  ReactFlowProvider,
  Position,
  EdgeLabelRenderer,
  getBezierPath,
  MarkerType,
} from 'reactflow';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Collapse,
  Card,
  CardContent,
  Grid,
  Avatar,
  useTheme,
  useMediaQuery,
  LinearProgress,
  IconButton,
  Tooltip,
  Divider,
  Button,
  Drawer,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  ChevronRight,
  School,
  Psychology,
  TrendingUp,
  TrendingDown,
  Visibility,
  Close,
} from '@mui/icons-material';
import 'reactflow/dist/style.css';

// Types
export interface PerformanceSubtopic {
  subtopic: string;
  best_score: number | null;
  worst_score: number | null;
  tests_taken: number;
  learning_status: string;
  trend: string;
  learned_percentage: number;
  last_tested: string;
  prerequisites?: any[];
  learning_material?: any[];
  dependents?: string[];
}

export interface PerformanceTopic {
  topic: string;
  subtopics: PerformanceSubtopic[];
  description?: string;
}

export interface PerformanceData {
  _id: string;
  class: string;
  subject: string;
  board: string;
  organisation_id: string;
  topics: PerformanceTopic[];
}

export interface PerformanceViewProps {
  data: PerformanceData;
  selectedNode?: 'subject' | 'topic' | 'subtopic';
  selectedTopic?: string;
  selectedSubtopic?: string;
  onNodeClick: (type: 'topic' | 'subtopic', name: string, data?: any) => void;
  connectedNodes?: { prerequisites: string[]; dependents: string[] };
}

// Performance Utilities
export const getPerformanceColor = (score: number | null): string => {
  if (score === null || score === undefined) return '#4f46e5';
  if (score >= 90) return '#3b82f6';
  if (score >= 75) return '#10b981';
  if (score >= 60) return '#f59e0b';
  if (score >= 40) return '#f97316';
  return '#ef4444';
};

export const getPerformanceLabel = (score: number | null): string => {
  if (score === null || score === undefined) return 'Not Tested';
  if (score >= 90) return '🌟 Excellent';
  if (score >= 75) return '✅ Good';
  if (score >= 60) return '📊 Moderate';
  if (score >= 40) return '📉 Below Average';
  return '⚠️ Needs Improvement';
};

export const adjustColor = (color: string, amount: number): string => {
  let hex = color.replace('#', '');
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  r = Math.max(0, Math.min(255, r + amount));
  g = Math.max(0, Math.min(255, g + amount));
  b = Math.max(0, Math.min(255, b + amount));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// Helper to flatten prerequisites
export const flattenPrerequisites = (prereqObj: any, parentId: string | null = null, level: number = 0): any[] => {
  const result: any[] = [];
  if (prereqObj && typeof prereqObj === 'object' && prereqObj.name) {
    result.push({
      id: prereqObj.id || `prereq_${Date.now()}_${Math.random()}`,
      name: prereqObj.name,
      parentId: parentId,
      level: level,
      hasChildren: prereqObj.children && prereqObj.children.length > 0,
    });
    if (prereqObj.children && prereqObj.children.length > 0) {
      prereqObj.children.forEach((child: any) => {
        result.push(...flattenPrerequisites(child, prereqObj.id || `prereq_${Date.now()}_${Math.random()}`, level + 1));
      });
    }
  }
  return result;
};

// Helper to find connected nodes
export const findConnectedNodes = (
  data: PerformanceData,
  subtopicName: string
): { prerequisites: string[]; dependents: string[] } => {
  const prerequisites: string[] = [];
  const dependents: string[] = [];

  let targetSubtopic: PerformanceSubtopic | undefined;

  for (const topic of data.topics) {
    const found = topic.subtopics?.find((st) => st.subtopic === subtopicName);
    if (found) {
      targetSubtopic = found;
      break;
    }
  }

  if (!targetSubtopic) return { prerequisites: [], dependents: [] };

  if (targetSubtopic.prerequisites) {
    targetSubtopic.prerequisites.forEach((prereq: any) => {
      if (typeof prereq === 'string') {
        if (!prerequisites.includes(prereq)) {
          prerequisites.push(prereq);
        }
      } else if (prereq.name) {
        if (!prerequisites.includes(prereq.name)) {
          prerequisites.push(prereq.name);
        }
        const flattened = flattenPrerequisites(prereq);
        flattened.forEach((f) => {
          if (!prerequisites.includes(f.name)) {
            prerequisites.push(f.name);
          }
        });
      }
    });
  }

  for (const topic of data.topics) {
    for (const subtopic of topic.subtopics || []) {
      if (subtopic.subtopic === subtopicName) continue;
      if (subtopic.prerequisites) {
        const hasPrereq = subtopic.prerequisites.some((p: any) => {
          if (typeof p === 'string') return p === subtopicName;
          if (p.name) {
            const flattened = flattenPrerequisites(p);
            return flattened.some((f) => f.name === subtopicName);
          }
          return false;
        });
        if (hasPrereq) {
          if (!dependents.includes(subtopic.subtopic)) {
            dependents.push(subtopic.subtopic);
          }
        }
      }
    }
  }

  return { prerequisites, dependents };
};

// ============================================
// CUSTOM EDGE WITH LABEL
// ============================================
const CustomEdge = ({ 
  id, 
  source, 
  target, 
  sourceX, 
  sourceY, 
  targetX, 
  targetY, 
  sourcePosition, 
  targetPosition, 
  style = {}, 
  data, 
  markerEnd 
}: any) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const label = data?.label || '';
  const labelColor = data?.labelColor || '#64748b';
  const edgeColor = data?.edgeColor || '#94a3b8';

  return (
    <>
      <path
        id={id}
        style={{
          ...style,
          stroke: edgeColor,
          strokeWidth: 2.5,
        }}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
      />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              background: 'rgba(255, 255, 255, 0.95)',
              padding: '2px 10px',
              borderRadius: '12px',
              fontSize: '10px',
              fontWeight: 600,
              color: labelColor,
              border: `1px solid ${labelColor}40`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              maxWidth: '150px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            className="nodrag nopan"
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

const edgeTypes = {
  custom: CustomEdge,
};

// ============================================
// SUBTOPIC DETAILS CONTENT
// ============================================
const SubtopicDetailsContent = ({ 
  fullData, 
  conn, 
  topicName, 
  onClose, 
  onViewDetails,
  isMobile 
}: any) => {
  if (!fullData) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error">No data available for this subtopic</Typography>
      </Box>
    );
  }

  const score = fullData?.best_score ?? null;
  const color = getPerformanceColor(score);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {fullData?.subtopic || 'Unknown Subtopic'}
        </Typography>
        {!isMobile && (
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Chip
        label={`From: ${topicName || 'Unknown Topic'}`}
        size="small"
        sx={{ mb: 2, bgcolor: '#4f46e5', color: '#fff' }}
      />

      {fullData?.learning_status && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Learning Status:
          </Typography>
          <Chip
            label={fullData.learning_status}
            size="small"
            sx={{
              ml: 1,
              bgcolor: fullData.learning_status === 'mastered' ? '#d1fae5' :
                       fullData.learning_status === 'in-progress' ? '#fef3c7' :
                       fullData.learning_status === 'not-started' ? '#fee2e2' : '#e5e7eb',
              color: fullData.learning_status === 'mastered' ? '#065f46' :
                     fullData.learning_status === 'in-progress' ? '#92400e' :
                     fullData.learning_status === 'not-started' ? '#991b1b' : '#374151',
              fontWeight: 600,
            }}
          />
        </Box>
      )}

      <Paper sx={{ p: 2, mb: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
              Tests Taken
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
              {fullData?.tests_taken ?? 0}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
              Best Score
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700, 
                color: score !== null ? color : '#94a3b8'
              }}
            >
              {score !== null ? `${score}%` : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
              Worst Score
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#94a3b8' }}>
              {fullData?.worst_score !== null && fullData?.worst_score !== undefined 
                ? `${fullData.worst_score}%` 
                : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
              Progress
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
              {fullData?.learned_percentage ?? 0}%
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          Progress: {fullData?.learned_percentage ?? 0}%
        </Typography>
        <LinearProgress
          variant="determinate"
          value={fullData?.learned_percentage ?? 0}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: '#e2e8f0',
            '& .MuiLinearProgress-bar': {
              bgcolor: color,
              borderRadius: 4,
            },
          }}
        />
      </Box>

      {fullData?.last_tested && fullData?.tests_taken > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Last Tested: <strong>{fullData.last_tested}</strong>
          </Typography>
        </Box>
      )}

      {fullData?.trend && fullData?.tests_taken > 0 && (
        <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#fefce8', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {fullData.trend === 'improving' ? (
              <TrendingUp sx={{ color: '#10b981' }} />
            ) : fullData.trend === 'declining' ? (
              <TrendingDown sx={{ color: '#ef4444' }} />
            ) : null}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Trend: {fullData.trend}
            </Typography>
          </Box>
        </Paper>
      )}

      {conn?.prerequisites && conn.prerequisites.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#5b21b6', mb: 1 }}>
            📚 Prerequisites (Before):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {conn.prerequisites.map((p: string, i: number) => (
              <Chip 
                key={i} 
                label={p} 
                size="small" 
                sx={{ 
                  bgcolor: '#ede9fe',
                  color: '#5b21b6',
                  fontWeight: 500,
                  border: '1px solid #c4b5fd',
                }} 
              />
            ))}
          </Box>
        </Box>
      )}

      {conn?.dependents && conn.dependents.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#065f46', mb: 1 }}>
            🎯 Dependents (After):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {conn.dependents.map((d: string, i: number) => (
              <Chip 
                key={i} 
                label={d} 
                size="small" 
                sx={{ 
                  bgcolor: '#d1fae5',
                  color: '#065f46',
                  fontWeight: 500,
                  border: '1px solid #6ee7b7',
                }} 
              />
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button
          fullWidth
          size="small"
          variant="contained"
          onClick={onViewDetails}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: '#4f46e5',
            '&:hover': { bgcolor: '#4338ca' },
          }}
        >
          <Visibility sx={{ mr: 0.5, fontSize: 16 }} />
          View Details
        </Button>
      </Box>
    </Box>
  );
};

// ============================================
// TOPIC DETAILS CONTENT
// ============================================
const TopicDetailsContent = ({ 
  topic, 
  onClose, 
  onViewDetails, 
  onSubtopicClick,
  isMobile 
}: any) => {
  if (!topic) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error">No data available for this topic</Typography>
      </Box>
    );
  }

  const totalSubtopics = topic?.subtopics?.length || 0;
  const masteredCount = topic?.subtopics?.filter(
    (st: any) => st?.best_score !== null && st?.best_score !== undefined && st.best_score >= 75
  ).length || 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          📚 {topic?.topic || 'Unknown Topic'}
        </Typography>
        {!isMobile && (
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Chip
          label={`${totalSubtopics} Subtopics`}
          size="small"
          sx={{ bgcolor: '#4f46e5', color: '#fff' }}
        />
        <Chip
          label={`${masteredCount} Mastered`}
          size="small"
          sx={{ bgcolor: '#10b981', color: '#fff' }}
        />
      </Box>

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Subtopics Performance:
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {topic?.subtopics?.map((st: any, idx: number) => {
          const score = st?.best_score ?? null;
          const color = getPerformanceColor(score);
          
          return (
            <Paper
              key={idx}
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: '#f8fafc',
                border: `2px solid ${color}`,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: '#f0f4ff',
                },
              }}
              onClick={() => onSubtopicClick(st)}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {st?.subtopic || 'Unknown'}
                </Typography>
                <Chip
                  label={score !== null ? `${score}%` : 'Not Tested'}
                  size="small"
                  sx={{ 
                    bgcolor: color,
                    color: '#fff',
                    fontWeight: 600,
                    minWidth: 70,
                  }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Tests: {st?.tests_taken || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Progress: {st?.learned_percentage || 0}%
                </Typography>
              </Box>
              
              <LinearProgress
                variant="determinate"
                value={st?.learned_percentage || 0}
                sx={{
                  mt: 1,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: '#e2e8f0',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: color,
                    borderRadius: 2,
                  },
                }}
              />
              
              {st?.prerequisites && st.prerequisites.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ color: '#5b21b6' }}>
                    📚 Prerequisites: {st.prerequisites.map((p: any) => typeof p === 'string' ? p : p?.name || '').join(', ')}
                  </Typography>
                </Box>
              )}
              
              {st?.learning_status && (
                <Chip
                  label={st.learning_status}
                  size="small"
                  sx={{
                    mt: 0.5,
                    bgcolor: st.learning_status === 'mastered' ? '#d1fae5' :
                             st.learning_status === 'in-progress' ? '#fef3c7' :
                             st.learning_status === 'not-started' ? '#fee2e2' : '#e5e7eb',
                    color: st.learning_status === 'mastered' ? '#065f46' :
                           st.learning_status === 'in-progress' ? '#92400e' :
                           st.learning_status === 'not-started' ? '#991b1b' : '#374151',
                    fontWeight: 600,
                    fontSize: '10px',
                  }}
                />
              )}
            </Paper>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button
          fullWidth
          size="small"
          variant="contained"
          onClick={onViewDetails}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: '#4f46e5',
            '&:hover': { bgcolor: '#4338ca' },
          }}
        >
          <Visibility sx={{ mr: 0.5, fontSize: 16 }} />
          View All Details
        </Button>
      </Box>
    </Box>
  );
};

// ============================================
// 1. FLOW VIEW - ReactFlow Canvas
// ============================================
export const PerformanceFlowView: React.FC<PerformanceViewProps> = ({
  data,
  selectedNode = 'subject',
  selectedTopic,
  selectedSubtopic,
  onNodeClick,
  connectedNodes = { prerequisites: [], dependents: [] },
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { fitView } = useReactFlow();
  
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedSubtopicData, setSelectedSubtopicData] = useState<any>(null);
  const [selectedTopicData, setSelectedTopicData] = useState<any>(null);
  const [showTopicDetails, setShowTopicDetails] = useState<boolean>(false);
  const [showSubtopicDetails, setShowSubtopicDetails] = useState<boolean>(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState<boolean>(false);
  const [detailsOpenedFromNode, setDetailsOpenedFromNode] = useState<boolean>(false);

  if (!data) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>No data available</Typography>
      </Box>
    );
  }

  const closeDetails = () => {
    setShowTopicDetails(false);
    setShowSubtopicDetails(false);
    setSelectedTopicData(null);
    setSelectedSubtopicData(null);
    setDetailDrawerOpen(false);
    setDetailsOpenedFromNode(false);
  };

  useEffect(() => {
    if (!data) return;

    const flowNodes: any[] = [];
    const flowEdges: any[] = [];
    const nodeSpacing = isMobile ? 80 : 120;
    const levelSpacing = isMobile ? 350 : 500;

    const showSubject = selectedNode === 'subject';
    const showTopicOnly = selectedNode === 'topic' && selectedTopic;
    const showSubtopicOnly = selectedNode === 'subtopic' && selectedSubtopic;

    if (showSubtopicOnly) {
      let foundSubtopic: PerformanceSubtopic | undefined;
      let foundTopic: string = '';
      let foundTopicData: PerformanceTopic | undefined;

      for (const topic of data.topics) {
        const found = topic.subtopics?.find((st) => st.subtopic === selectedSubtopic);
        if (found) {
          foundSubtopic = found;
          foundTopic = topic.topic;
          foundTopicData = topic;
          break;
        }
      }

      if (foundSubtopic && foundTopicData) {
        const score = foundSubtopic.best_score;
        const color = getPerformanceColor(score);
        
        flowNodes.push({
          id: 'subtopic_root',
          data: {
            label: foundSubtopic.subtopic,
            type: 'subtopic',
            score: score,
            fullData: foundSubtopic,
            color: color,
            topicName: foundTopic,
            isRoot: true,
            testsTaken: foundSubtopic.tests_taken || 0,
          },
          position: { x: 300, y: 300 },
          sourcePosition: Position.Right,
          targetPosition: Position.Left,
          style: {
            background: `linear-gradient(135deg, ${color} 0%, ${adjustColor(color, -15)} 100%)`,
            border: `3px solid ${color}`,
            borderRadius: '16px',
            padding: '14px 24px',
            fontSize: isMobile ? 12 : 14,
            fontWeight: 700,
            color: '#ffffff',
            cursor: 'default',
            boxShadow: `0 6px 24px ${color}55`,
            minWidth: isMobile ? 140 : 200,
            textAlign: 'center',
          },
        });

        const connNodes = findConnectedNodes(data, selectedSubtopic);
        if (connNodes.prerequisites.length > 0) {
          const prereqXPos = 300 + levelSpacing / 1.5;
          const prereqStartY = 300 - ((connNodes.prerequisites.length - 1) * nodeSpacing) / 2;

          connNodes.prerequisites.forEach((prereq: string, idx: number) => {
            const prereqNodeId = `prereq_${idx}`;
            const prereqColor = '#8b5cf6';
            
            flowNodes.push({
              id: prereqNodeId,
              data: {
                label: `📚 ${prereq}`,
                type: 'prerequisite',
                isPrereq: true,
              },
              position: { 
                x: prereqXPos, 
                y: prereqStartY + idx * nodeSpacing 
              },
              sourcePosition: Position.Right,
              targetPosition: Position.Left,
              style: {
                background: 'linear-gradient(135deg, #ede9fe 0%, #c4b5fd 100%)',
                border: `2px solid ${prereqColor}`,
                borderRadius: '12px',
                padding: '8px 14px',
                fontSize: isMobile ? 9 : 11,
                fontWeight: 600,
                color: '#5b21b6',
                cursor: 'default',
                boxShadow: '0 2px 12px rgba(139, 92, 246, 0.25)',
                minWidth: isMobile ? 80 : 120,
                textAlign: 'center',
              },
            });

            // Edge without label for prerequisites
            flowEdges.push({
              id: `subtopic_root-prereq_${idx}`,
              source: 'subtopic_root',
              target: prereqNodeId,
              type: 'custom',
              data: {
                label: '', // Empty label to hide text
                labelColor: '#8b5cf6',
                edgeColor: '#8b5cf6',
              },
              style: {
                stroke: '#8b5cf6',
                strokeWidth: 2,
                strokeDasharray: '6,4',
              },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: '#8b5cf6',
              },
              animated: false,
            });
          });
        }

        if (detailsOpenedFromNode) {
          setSelectedSubtopicData({ ...foundSubtopic, connectedNodes: connNodes, topicName: foundTopic });
          setShowSubtopicDetails(true);
          if (isMobile || isTablet) {
            setDetailDrawerOpen(true);
          }
        }
      }
    }
    else if (showTopicOnly) {
      const topicData = data.topics.find((t) => t.topic === selectedTopic);
      
      if (topicData) {
        const totalSubtopics = topicData.subtopics?.length || 0;
        const masteredCount = topicData.subtopics?.filter(
          (st: any) => st.best_score !== null && st.best_score >= 75
        ).length || 0;

        flowNodes.push({
          id: 'topic_root',
          data: {
            label: topicData.topic,
            type: 'topic',
            subtopicCount: totalSubtopics,
            masteredCount: masteredCount,
            fullData: topicData,
            isRoot: true,
          },
          position: { x: 100, y: 300 },
          sourcePosition: Position.Right,
          targetPosition: Position.Left,
          style: {
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            border: '3px solid #4f46e5',
            borderRadius: '20px',
            padding: '16px 28px',
            fontSize: isMobile ? 14 : 18,
            fontWeight: 700,
            color: '#ffffff',
            cursor: 'default',
            boxShadow: '0 6px 24px rgba(79, 70, 229, 0.4)',
            minWidth: isMobile ? 140 : 200,
            textAlign: 'center',
          },
        });

        const subtopics = topicData.subtopics || [];
        const subXPos = 100 + levelSpacing;
        
        subtopics.forEach((st, stIdx) => {
          const score = st.best_score;
          const color = getPerformanceColor(score);
          
          const totalSubtopicsCount = subtopics.length;
          const subYPos = 300 + (stIdx - (totalSubtopicsCount - 1) / 2) * nodeSpacing;

          const subNodeId = `subtopic_${stIdx}`;
          flowNodes.push({
            id: subNodeId,
            data: {
              label: st.subtopic,
              type: 'subtopic',
              score: score,
              fullData: st,
              color: color,
              topicName: topicData.topic,
              testsTaken: st.tests_taken || 0,
            },
            position: { x: subXPos, y: subYPos },
            sourcePosition: Position.Right,
            targetPosition: Position.Left,
            style: {
              background: `linear-gradient(135deg, ${color} 0%, ${adjustColor(color, -15)} 100%)`,
              border: `2px solid ${color}`,
              borderRadius: '14px',
              padding: '10px 16px',
              fontSize: isMobile ? 10 : 12,
              fontWeight: 600,
              color: '#ffffff',
              cursor: 'default',
              boxShadow: `0 4px 16px ${color}55`,
              minWidth: isMobile ? 100 : 140,
              maxWidth: isMobile ? 140 : 200,
              textAlign: 'center',
            },
          });

          flowEdges.push({
            id: `topic_root-${subNodeId}`,
            source: 'topic_root',
            target: subNodeId,
            type: 'custom',
            data: {
              label: score !== null ? `${score}%` : 'Not Tested',
              labelColor: color,
              edgeColor: color,
            },
            style: {
              stroke: color,
              strokeWidth: 2,
              strokeDasharray: '5,5',
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: color,
            },
            animated: false,
          });

          if (st.prerequisites && st.prerequisites.length > 0) {
            const connNodes = findConnectedNodes(data, st.subtopic);
            const prereqXPos = subXPos + levelSpacing / 1.5;
            const prereqStartY = subYPos - ((connNodes.prerequisites.length - 1) * nodeSpacing) / 2;
            const prereqColor = '#8b5cf6';

            connNodes.prerequisites.forEach((prereq: string, prereqIdx: number) => {
              const prereqNodeId = `prereq_${stIdx}_${prereqIdx}`;
              
              flowNodes.push({
                id: prereqNodeId,
                data: {
                  label: `📚 ${prereq}`,
                  type: 'prerequisite',
                  isPrereq: true,
                },
                position: { 
                  x: prereqXPos, 
                  y: prereqStartY + prereqIdx * nodeSpacing 
                },
                sourcePosition: Position.Right,
                targetPosition: Position.Left,
                style: {
                  background: 'linear-gradient(135deg, #ede9fe 0%, #c4b5fd 100%)',
                  border: `2px solid ${prereqColor}`,
                  borderRadius: '12px',
                  padding: '8px 14px',
                  fontSize: isMobile ? 9 : 11,
                  fontWeight: 600,
                  color: '#5b21b6',
                  cursor: 'default',
                  boxShadow: '0 2px 12px rgba(139, 92, 246, 0.25)',
                  minWidth: isMobile ? 80 : 120,
                  textAlign: 'center',
                },
              });

              // Edge without label for prerequisites
              flowEdges.push({
                id: `${subNodeId}-${prereqNodeId}`,
                source: subNodeId,
                target: prereqNodeId,
                type: 'custom',
                data: {
                  label: '', // Empty label to hide text
                  labelColor: '#8b5cf6',
                  edgeColor: '#8b5cf6',
                },
                style: {
                  stroke: '#8b5cf6',
                  strokeWidth: 2,
                  strokeDasharray: '6,4',
                },
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  color: '#8b5cf6',
                },
                animated: false,
              });
            });
          }
        });

        if (detailsOpenedFromNode) {
          setSelectedTopicData({ ...topicData });
          setShowTopicDetails(true);
          if (isMobile || isTablet) {
            setDetailDrawerOpen(true);
          }
        }
      }
    }
    else {
      flowNodes.push({
        id: 'subject_root',
        data: {
          label: data.subject,
          type: 'subject',
          class: data.class,
          score: null,
        },
        position: { x: 100, y: 300 },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        style: {
          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
          border: '3px solid #4f46e5',
          borderRadius: '20px',
          padding: '16px 28px',
          fontSize: isMobile ? 14 : 18,
          fontWeight: 700,
          color: '#ffffff',
          cursor: 'default',
          boxShadow: '0 6px 24px rgba(79, 70, 229, 0.4)',
          minWidth: isMobile ? 140 : 200,
          textAlign: 'center',
        },
      });

      data.topics.forEach((topic, topicIdx) => {
        const totalSubtopics = topic.subtopics?.length || 0;
        const masteredCount = topic.subtopics?.filter(
          (st) => st.best_score !== null && st.best_score >= 75
        ).length || 0;
        const xPos = 100 + levelSpacing;
        const yPos = 100 + topicIdx * nodeSpacing * 2.5;

        const topicNodeId = `topic_${topicIdx}`;
        flowNodes.push({
          id: topicNodeId,
          data: {
            label: topic.topic,
            type: 'topic',
            subtopicCount: totalSubtopics,
            masteredCount: masteredCount,
            fullData: topic,
          },
          position: { x: xPos, y: yPos },
          sourcePosition: Position.Right,
          targetPosition: Position.Left,
          style: {
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            border: '2px solid #4f46e5',
            borderRadius: '16px',
            padding: '12px 20px',
            fontSize: isMobile ? 12 : 14,
            fontWeight: 600,
            color: '#ffffff',
            cursor: 'default',
            boxShadow: '0 4px 16px rgba(79, 70, 229, 0.3)',
            minWidth: isMobile ? 120 : 160,
            textAlign: 'center',
          },
        });

        flowEdges.push({
          id: `subject-topic_${topicIdx}`,
          source: 'subject_root',
          target: topicNodeId,
          type: 'custom',
          data: {
            label: `Topic ${topicIdx + 1}`,
            labelColor: '#4f46e5',
            edgeColor: '#4f46e5',
          },
          style: {
            stroke: '#4f46e5',
            strokeWidth: 2,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#4f46e5',
          },
          animated: false,
        });

        topic.subtopics?.forEach((st, stIdx) => {
          const score = st.best_score;
          const color = getPerformanceColor(score);
          
          const subXPos = xPos + levelSpacing;
          const totalSubtopicsInTopic = topic.subtopics?.length || 0;
          const subYPos = yPos + (stIdx - (totalSubtopicsInTopic - 1) / 2) * nodeSpacing;

          const subNodeId = `subtopic_${topicIdx}_${stIdx}`;
          flowNodes.push({
            id: subNodeId,
            data: {
              label: st.subtopic,
              type: 'subtopic',
              score: score,
              fullData: st,
              color: color,
              topicName: topic.topic,
              testsTaken: st.tests_taken || 0,
            },
            position: { x: subXPos, y: subYPos },
            sourcePosition: Position.Right,
            targetPosition: Position.Left,
            style: {
              background: `linear-gradient(135deg, ${color} 0%, ${adjustColor(color, -15)} 100%)`,
              border: `2px solid ${color}`,
              borderRadius: '14px',
              padding: '10px 16px',
              fontSize: isMobile ? 10 : 12,
              fontWeight: 600,
              color: '#ffffff',
              cursor: 'default',
              boxShadow: `0 4px 16px ${color}55`,
              minWidth: isMobile ? 100 : 140,
              maxWidth: isMobile ? 140 : 200,
              textAlign: 'center',
            },
          });

          flowEdges.push({
            id: `${topicNodeId}-${subNodeId}`,
            source: topicNodeId,
            target: subNodeId,
            type: 'custom',
            data: {
              label: score !== null ? `${score}%` : 'Not Tested',
              labelColor: color,
              edgeColor: color,
            },
            style: {
              stroke: color,
              strokeWidth: 2,
              strokeDasharray: '5,5',
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: color,
            },
            animated: false,
          });

          if (st.prerequisites && st.prerequisites.length > 0) {
            const connNodes = findConnectedNodes(data, st.subtopic);
            const prereqXPos = subXPos + levelSpacing / 1.5;
            const prereqStartY = subYPos - ((connNodes.prerequisites.length - 1) * nodeSpacing) / 2;
            const prereqColor = '#8b5cf6';

            connNodes.prerequisites.forEach((prereq: string, prereqIdx: number) => {
              const prereqNodeId = `prereq_${topicIdx}_${stIdx}_${prereqIdx}`;
              
              flowNodes.push({
                id: prereqNodeId,
                data: {
                  label: `📚 ${prereq}`,
                  type: 'prerequisite',
                  isPrereq: true,
                },
                position: { 
                  x: prereqXPos, 
                  y: prereqStartY + prereqIdx * nodeSpacing 
                },
                sourcePosition: Position.Right,
                targetPosition: Position.Left,
                style: {
                  background: 'linear-gradient(135deg, #ede9fe 0%, #c4b5fd 100%)',
                  border: `2px solid ${prereqColor}`,
                  borderRadius: '12px',
                  padding: '8px 14px',
                  fontSize: isMobile ? 9 : 11,
                  fontWeight: 600,
                  color: '#5b21b6',
                  cursor: 'default',
                  boxShadow: '0 2px 12px rgba(139, 92, 246, 0.25)',
                  minWidth: isMobile ? 80 : 120,
                  textAlign: 'center',
                },
              });

              // Edge without label for prerequisites
              flowEdges.push({
                id: `${subNodeId}-${prereqNodeId}`,
                source: subNodeId,
                target: prereqNodeId,
                type: 'custom',
                data: {
                  label: '', // Empty label to hide text
                  labelColor: '#8b5cf6',
                  edgeColor: '#8b5cf6',
                },
                style: {
                  stroke: '#8b5cf6',
                  strokeWidth: 2,
                  strokeDasharray: '6,4',
                },
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  color: '#8b5cf6',
                },
                animated: false,
              });
            });
          }
        });
      });
    }

    setNodes(flowNodes);
    setEdges(flowEdges);

    setTimeout(() => {
      if (reactFlowWrapper.current) {
        fitView({ padding: 0.2, duration: 300 });
      }
    }, 100);
  }, [data, selectedNode, selectedTopic, selectedSubtopic, isMobile, fitView, detailsOpenedFromNode]);

  const handleTopicClick = (topicData: any) => {
    setDetailsOpenedFromNode(true);
    setSelectedTopicData(topicData);
    setShowTopicDetails(true);
    setShowSubtopicDetails(false);
    setSelectedSubtopicData(null);
    if (isMobile || isTablet) {
      setDetailDrawerOpen(true);
    }
    onNodeClick('topic', topicData.topic, topicData);
  };

  const handleSubtopicClick = (subtopicData: any, topicName: string) => {
    setDetailsOpenedFromNode(true);
    const connNodes = findConnectedNodes(data, subtopicData.subtopic);
    setSelectedSubtopicData({ ...subtopicData, connectedNodes: connNodes, topicName: topicName });
    setShowSubtopicDetails(true);
    setShowTopicDetails(false);
    setSelectedTopicData(null);
    if (isMobile || isTablet) {
      setDetailDrawerOpen(true);
    }
    onNodeClick('subtopic', subtopicData.subtopic, subtopicData);
  };

  const closeTopicDetails = () => {
    setShowTopicDetails(false);
    setSelectedTopicData(null);
    setDetailsOpenedFromNode(false);
    if (isMobile || isTablet) {
      setDetailDrawerOpen(false);
    }
  };

  const closeSubtopicDetails = () => {
    setShowSubtopicDetails(false);
    setSelectedSubtopicData(null);
    setDetailsOpenedFromNode(false);
    if (isMobile || isTablet) {
      setDetailDrawerOpen(false);
    }
  };

  const handleNodeClick = (event: any, node: any) => {
    if (node.data.type === 'topic') {
      handleTopicClick(node.data.fullData);
    } else if (node.data.type === 'subtopic') {
      handleSubtopicClick(node.data.fullData, node.data.topicName || '');
    }
  };

  const renderDesktopDetailsPanel = () => {
    if (showSubtopicDetails && selectedSubtopicData) {
      const { fullData, connectedNodes: conn, topicName } = selectedSubtopicData;
      return (
        <Paper
          sx={{
            position: 'absolute',
            right: 20,
            top: 20,
            width: isMobile ? '90%' : 400,
            maxHeight: '80%',
            overflowY: 'auto',
            p: 3,
            bgcolor: '#ffffff',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            zIndex: 10,
            border: '1px solid #e2e8f0',
            display: isMobile ? 'none' : 'block',
          }}
        >
          <SubtopicDetailsContent 
            fullData={fullData} 
            conn={conn} 
            topicName={topicName} 
            onClose={closeSubtopicDetails}
            onViewDetails={() => {
              onNodeClick('subtopic', fullData?.subtopic, fullData);
            }}
            isMobile={isMobile}
          />
        </Paper>
      );
    }

    if (showTopicDetails && selectedTopicData) {
      return (
        <Paper
          sx={{
            position: 'absolute',
            right: 20,
            top: 20,
            width: isMobile ? '90%' : 400,
            maxHeight: '80%',
            overflowY: 'auto',
            p: 3,
            bgcolor: '#ffffff',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            zIndex: 10,
            border: '1px solid #e2e8f0',
            display: isMobile ? 'none' : 'block',
          }}
        >
          <TopicDetailsContent
            topic={selectedTopicData}
            onClose={closeTopicDetails}
            onViewDetails={() => {
              onNodeClick('topic', selectedTopicData.topic, selectedTopicData);
            }}
            onSubtopicClick={(st: any) => {
              handleSubtopicClick(st, selectedTopicData.topic);
            }}
            isMobile={isMobile}
          />
        </Paper>
      );
    }

    return null;
  };

  const renderMobileDrawer = () => {
    if (!detailDrawerOpen || !detailsOpenedFromNode) return null;

    let content = null;
    if (showSubtopicDetails && selectedSubtopicData) {
      const { fullData, connectedNodes: conn, topicName } = selectedSubtopicData;
      content = (
        <SubtopicDetailsContent
          fullData={fullData}
          conn={conn}
          topicName={topicName}
          onClose={closeSubtopicDetails}
          onViewDetails={() => {
            onNodeClick('subtopic', fullData?.subtopic, fullData);
            closeSubtopicDetails();
          }}
          isMobile={isMobile}
        />
      );
    } else if (showTopicDetails && selectedTopicData) {
      content = (
        <TopicDetailsContent
          topic={selectedTopicData}
          onClose={closeTopicDetails}
          onViewDetails={() => {
            onNodeClick('topic', selectedTopicData.topic, selectedTopicData);
            closeTopicDetails();
          }}
          onSubtopicClick={(st: any) => {
            handleSubtopicClick(st, selectedTopicData.topic);
          }}
          isMobile={isMobile}
        />
      );
    }

    if (!content) return null;

    return (
      <Drawer
        anchor="bottom"
        open={detailDrawerOpen}
        onClose={closeDetails}
        PaperProps={{
          sx: {
            maxHeight: '80vh',
            minHeight: '40vh',
            borderRadius: '20px 20px 0 0',
            p: 2,
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2,
          flexShrink: 0,
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {showTopicDetails ? '📚 Topic Details' : '📖 Subtopic Details'}
          </Typography>
          <IconButton onClick={closeDetails}>
            <Close />
          </IconButton>
        </Box>
        <Divider sx={{ flexShrink: 0 }} />
        <Box sx={{ 
          mt: 2, 
          overflowY: 'auto', 
          flex: 1,
          pb: 2,
        }}>
          {content}
        </Box>
      </Drawer>
    );
  };

  return (
    <Box
      ref={reactFlowWrapper}
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: '#f8fafc',
        position: 'relative',
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        edgeTypes={edgeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        fitView={true}
        style={{ background: '#f8fafc' }}
        minZoom={0.1}
        maxZoom={2}
        attributionPosition="bottom-right"
        zoomOnScroll={true}
        zoomOnPinch={true}
        panOnScroll={true}
        panOnDrag={true}
      >
        <Controls
          showZoom={true}
          showFitView={true}
          showInteractive={false}
          position="bottom-left"
          style={{
            button: {
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            },
          }}
        />
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#cbd5e1" />
      </ReactFlow>

      {renderDesktopDetailsPanel()}
      {renderMobileDrawer()}
    </Box>
  );
};

// Wrapper component with ReactFlowProvider
export const PerformanceFlowViewWrapper: React.FC<PerformanceViewProps> = (props) => {
  return (
    <ReactFlowProvider>
      <PerformanceFlowView {...props} />
    </ReactFlowProvider>
  );
};

// ============================================
// 2. TREE VIEW
// ============================================
export const PerformanceTreeView: React.FC<PerformanceViewProps> = ({
  data,
  selectedNode,
  selectedTopic,
  selectedSubtopic,
  onNodeClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());
  const [expandedSubtopics, setExpandedSubtopics] = useState<Set<string>>(new Set());

  if (!data) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>No data available</Typography>
      </Box>
    );
  }

  const getFilteredData = () => {
    if (selectedNode === 'subtopic' && selectedSubtopic) {
      for (const topic of data.topics) {
        const found = topic.subtopics?.find((st) => st.subtopic === selectedSubtopic);
        if (found) {
          return {
            ...data,
            topics: [{
              ...topic,
              subtopics: [found],
            }],
          };
        }
      }
    }
    if (selectedNode === 'topic' && selectedTopic) {
      const topicData = data.topics.find((t) => t.topic === selectedTopic);
      if (topicData) {
        return {
          ...data,
          topics: [topicData],
        };
      }
    }
    return data;
  };

  const filteredData = getFilteredData();

  const toggleTopic = (topicName: string) => {
    const newSet = new Set(expandedTopics);
    if (newSet.has(topicName)) {
      newSet.delete(topicName);
    } else {
      newSet.add(topicName);
    }
    setExpandedTopics(newSet);
  };

  const toggleSubtopic = (subtopicName: string) => {
    const newSet = new Set(expandedSubtopics);
    if (newSet.has(subtopicName)) {
      newSet.delete(subtopicName);
    } else {
      newSet.add(subtopicName);
    }
    setExpandedSubtopics(newSet);
  };

  const renderPrerequisiteNode = (prereq: any, idx: number, level: number = 0) => {
    if (typeof prereq === 'string') {
      return (
        <Box key={idx} sx={{
          background: '#ede9fe',
          borderRadius: '8px',
          padding: '8px 12px',
          marginLeft: level * 20,
          marginBottom: '4px',
          borderLeft: '3px solid #8b5cf6',
          fontSize: '13px',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Psychology sx={{ fontSize: 14, color: '#5b21b6' }} />
            <Typography variant="body2" sx={{ color: '#5b21b6' }}>{prereq}</Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box key={idx} sx={{ mb: 1 }}>
        <Box sx={{
          background: '#ede9fe',
          borderRadius: '8px',
          padding: '8px 12px',
          marginLeft: level * 20,
          marginBottom: '4px',
          borderLeft: '3px solid #8b5cf6',
          fontSize: '13px',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Psychology sx={{ fontSize: 14, color: '#5b21b6' }} />
            <Typography variant="body2" sx={{ fontWeight: level === 0 ? 600 : 400, color: '#5b21b6' }}>
              {prereq.name}
            </Typography>
          </Box>
        </Box>
        {prereq.children && prereq.children.length > 0 && (
          <Box sx={{ ml: 2 }}>
            {prereq.children.map((child: any, childIndex: number) =>
              renderPrerequisiteNode(child, childIndex, level + 1)
            )}
          </Box>
        )}
      </Box>
    );
  };

  const handleItemClick = (type: 'topic' | 'subtopic', name: string, data: any) => {
    onNodeClick(type, name, data);
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        overflowY: 'auto',
        height: '100%',
        bgcolor: '#f8fafc',
      }}
    >
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {selectedNode === 'subject' && (
          <Paper
            sx={{
              p: 2,
              mb: 3,
              bgcolor: '#4f46e5',
              color: '#fff',
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <School sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {data.subject}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Class {data.class}
              </Typography>
            </Box>
          </Paper>
        )}

        {filteredData.topics.map((topic, idx) => {
          const totalSubtopics = topic.subtopics?.length || 0;
          const masteredCount = topic.subtopics?.filter(
            (st) => st.best_score !== null && st.best_score >= 75
          ).length || 0;
          const isExpanded = expandedTopics.has(topic.topic);
          const isTopicSelected = selectedNode === 'topic' && selectedTopic === topic.topic;

          return (
            <Box key={idx} sx={{ mb: 2 }}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `2px solid ${isTopicSelected ? '#10b981' : '#4f46e5'}`,
                  bgcolor: isTopicSelected ? '#f0fdf4' : (isExpanded ? '#f0f4ff' : '#fff'),
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                  },
                }}
                onClick={() => {
                  toggleTopic(topic.topic);
                  handleItemClick('topic', topic.topic, topic);
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: isTopicSelected ? '#10b981' : '#4f46e5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 14,
                    }}
                  >
                    {idx + 1}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      {topic.topic}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {masteredCount}/{totalSubtopics} mastered
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Chip
                    label={isExpanded ? 'Collapse' : 'Expand'}
                    size="small"
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </Box>
              </Paper>

              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    ml: { xs: 2, sm: 4 },
                    mt: 1,
                    pl: 2,
                    borderLeft: `3px solid ${isTopicSelected ? '#10b981' : '#4f46e5'}`,
                  }}
                >
                  {topic.subtopics?.map((st, stIdx) => {
                    const score = st.best_score;
                    const color = getPerformanceColor(score);
                    const isSubtopicExpanded = expandedSubtopics.has(st.subtopic);
                    const hasPrerequisites = st.prerequisites && st.prerequisites.length > 0;
                    const isSubtopicSelected = selectedNode === 'subtopic' && selectedSubtopic === st.subtopic;

                    const conn = findConnectedNodes(data, st.subtopic);

                    return (
                      <Paper
                        key={stIdx}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          borderRadius: 2,
                          bgcolor: isSubtopicSelected ? `${color}22` : '#fafbfc',
                          border: `2px solid ${isSubtopicSelected ? color : '#e2e8f0'}`,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: '#f0f4ff',
                          },
                        }}
                        onClick={() => {
                          if (hasPrerequisites) {
                            toggleSubtopic(st.subtopic);
                          }
                          handleItemClick('subtopic', st.subtopic, st);
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                bgcolor: color,
                                opacity: 0.7,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              {stIdx + 1}
                            </Box>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {st.subtopic}
                              </Typography>
                              {score !== null && (
                                <Chip
                                  label={`${score}%`}
                                  size="small"
                                  sx={{
                                    bgcolor: color,
                                    color: '#fff',
                                    fontWeight: 600,
                                    height: 20,
                                    fontSize: '10px',
                                  }}
                                />
                              )}
                              <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                                Tests: {st.tests_taken || 0} | Progress: {st.learned_percentage || 0}%
                              </Typography>
                              {st.learning_status && (
                                <Chip
                                  label={st.learning_status}
                                  size="small"
                                  sx={{
                                    mt: 0.5,
                                    bgcolor: st.learning_status === 'mastered' ? '#d1fae5' :
                                             st.learning_status === 'in-progress' ? '#fef3c7' :
                                             st.learning_status === 'not-started' ? '#fee2e2' : '#e5e7eb',
                                    color: st.learning_status === 'mastered' ? '#065f46' :
                                           st.learning_status === 'in-progress' ? '#92400e' :
                                           st.learning_status === 'not-started' ? '#991b1b' : '#374151',
                                    fontWeight: 600,
                                    fontSize: '10px',
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleItemClick('subtopic', st.subtopic, st);
                                }}
                              >
                                <ChevronRight sx={{ color: '#94a3b8', fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                            {hasPrerequisites && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSubtopic(st.subtopic);
                                }}
                              >
                                {isSubtopicExpanded ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            )}
                          </Box>
                        </Box>

                        {hasPrerequisites && (
                          <Collapse in={isSubtopicExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 1, ml: 2, pl: 2, borderLeft: '2px dashed #8b5cf6' }}>
                              <Typography variant="caption" sx={{ color: '#5b21b6', fontWeight: 600, display: 'block', mb: 1 }}>
                                📚 Prerequisites (Before):
                              </Typography>
                              {st.prerequisites.map((prereq, prereqIdx) =>
                                renderPrerequisiteNode(prereq, prereqIdx)
                              )}
                            </Box>
                          </Collapse>
                        )}

                        {(conn.prerequisites.length > 0 || conn.dependents.length > 0) && (
                          <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>
                              🔗 Connected Concepts:
                            </Typography>
                            {conn.prerequisites.length > 0 && (
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="caption" sx={{ color: '#5b21b6' }}>
                                  📚 Before: {conn.prerequisites.join(', ')}
                                </Typography>
                              </Box>
                            )}
                            {conn.dependents.length > 0 && (
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="caption" sx={{ color: '#065f46' }}>
                                  🎯 After: {conn.dependents.join(', ')}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ============================================
// 3. GRID VIEW
// ============================================
export const PerformanceGridView: React.FC<PerformanceViewProps> = ({
  data,
  selectedNode,
  selectedTopic,
  selectedSubtopic,
  onNodeClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!data) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>No data available</Typography>
      </Box>
    );
  }

  const getFilteredData = () => {
    if (selectedNode === 'subtopic' && selectedSubtopic) {
      for (const topic of data.topics) {
        const found = topic.subtopics?.find((st) => st.subtopic === selectedSubtopic);
        if (found) {
          return {
            ...data,
            topics: [{
              ...topic,
              subtopics: [found],
            }],
          };
        }
      }
    }
    if (selectedNode === 'topic' && selectedTopic) {
      const topicData = data.topics.find((t) => t.topic === selectedTopic);
      if (topicData) {
        return {
          ...data,
          topics: [topicData],
        };
      }
    }
    return data;
  };

  const filteredData = getFilteredData();

  const handleCardClick = (type: 'topic' | 'subtopic', name: string, data: any) => {
    onNodeClick(type, name, data);
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        overflowY: 'auto',
        height: '100%',
        bgcolor: '#f8fafc',
      }}
    >
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
        {selectedNode === 'subject' && data.subject} 
        {selectedNode === 'topic' && `${selectedTopic} - Performance`}
        {selectedNode === 'subtopic' && `${selectedSubtopic} - Details`}
      </Typography>

      <Grid container spacing={3}>
        {filteredData.topics.map((topic, idx) => {
          const totalSubtopics = topic.subtopics?.length || 0;
          const masteredCount = topic.subtopics?.filter(
            (st) => st.best_score !== null && st.best_score >= 75
          ).length || 0;

          return (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: `2px solid ${selectedNode === 'topic' && selectedTopic === topic.topic ? '#10b981' : '#4f46e5'}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(79, 70, 229, 0.2)',
                  },
                }}
                onClick={() => handleCardClick('topic', topic.topic, topic)}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 2,
                      pb: 1.5,
                      borderBottom: `2px solid ${selectedNode === 'topic' && selectedTopic === topic.topic ? '#10b981' : '#4f46e5'}`,
                    }}
                  >
                    <Avatar sx={{ bgcolor: selectedNode === 'topic' && selectedTopic === topic.topic ? '#10b981' : '#4f46e5', width: 40, height: 40 }}>
                      {idx + 1}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, fontSize: isMobile ? 14 : 16 }}
                      >
                        {topic.topic}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        {masteredCount}/{totalSubtopics} mastered
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {topic.subtopics?.map((st, stIdx) => {
                      const score = st.best_score;
                      const color = getPerformanceColor(score);
                      const isSelected = selectedNode === 'subtopic' && selectedSubtopic === st.subtopic;
                      const conn = findConnectedNodes(data, st.subtopic);

                      return (
                        <Paper
                          key={stIdx}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: isSelected ? `${color}22` : '#fafbfc',
                            border: `2px solid ${isSelected ? color : '#e2e8f0'}`,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: '#f0f4ff',
                              borderColor: '#4f46e5',
                            },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCardClick('subtopic', st.subtopic, st);
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {st.subtopic}
                            </Typography>
                            <Chip
                              label={score !== null ? `${score}%` : 'Not Tested'}
                              size="small"
                              sx={{
                                bgcolor: color,
                                color: '#fff',
                                fontWeight: 600,
                                minWidth: 70,
                              }}
                            />
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={st.learned_percentage || 0}
                            sx={{
                              mt: 1,
                              height: 4,
                              borderRadius: 2,
                              bgcolor: '#e2e8f0',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: color,
                                borderRadius: 2,
                              },
                            }}
                          />
                          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.5 }}>
                            Tests: {st.tests_taken || 0} | Progress: {st.learned_percentage || 0}%
                          </Typography>
                          {st.learning_status && (
                            <Chip
                              label={st.learning_status}
                              size="small"
                              sx={{
                                mt: 0.5,
                                bgcolor: st.learning_status === 'mastered' ? '#d1fae5' :
                                         st.learning_status === 'in-progress' ? '#fef3c7' :
                                         st.learning_status === 'not-started' ? '#fee2e2' : '#e5e7eb',
                                color: st.learning_status === 'mastered' ? '#065f46' :
                                       st.learning_status === 'in-progress' ? '#92400e' :
                                       st.learning_status === 'not-started' ? '#991b1b' : '#374151',
                                fontWeight: 600,
                                fontSize: '10px',
                              }}
                            />
                          )}
                          {st.prerequisites && st.prerequisites.length > 0 && (
                            <Typography variant="caption" sx={{ color: '#5b21b6', display: 'block', mt: 1 }}>
                              📚 Prerequisites: {st.prerequisites.map((p: any) => typeof p === 'string' ? p : p?.name || '').join(', ')}
                            </Typography>
                          )}
                          {(conn.prerequisites.length > 0 || conn.dependents.length > 0) && (
                            <Box sx={{ mt: 1 }}>
                              {conn.prerequisites.length > 0 && (
                                <Typography variant="caption" sx={{ color: '#5b21b6', display: 'block' }}>
                                  📚 Before: {conn.prerequisites.join(', ')}
                                </Typography>
                              )}
                              {conn.dependents.length > 0 && (
                                <Typography variant="caption" sx={{ color: '#065f46', display: 'block' }}>
                                  🎯 After: {conn.dependents.join(', ')}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

// ============================================
// 4. LIST VIEW
// ============================================
export const PerformanceListView: React.FC<PerformanceViewProps> = ({
  data,
  selectedNode,
  selectedTopic,
  selectedSubtopic,
  onNodeClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());
  const [expandedSubtopics, setExpandedSubtopics] = useState<Set<string>>(new Set());

  if (!data) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>No data available</Typography>
      </Box>
    );
  }

  const getFilteredData = () => {
    if (selectedNode === 'subtopic' && selectedSubtopic) {
      for (const topic of data.topics) {
        const found = topic.subtopics?.find((st) => st.subtopic === selectedSubtopic);
        if (found) {
          return {
            ...data,
            topics: [{
              ...topic,
              subtopics: [found],
            }],
          };
        }
      }
    }
    if (selectedNode === 'topic' && selectedTopic) {
      const topicData = data.topics.find((t) => t.topic === selectedTopic);
      if (topicData) {
        return {
          ...data,
          topics: [topicData],
        };
      }
    }
    return data;
  };

  const filteredData = getFilteredData();

  const toggleTopic = (topicName: string) => {
    const newSet = new Set(expandedTopics);
    if (newSet.has(topicName)) {
      newSet.delete(topicName);
    } else {
      newSet.add(topicName);
    }
    setExpandedTopics(newSet);
  };

  const toggleSubtopic = (subtopicName: string) => {
    const newSet = new Set(expandedSubtopics);
    if (newSet.has(subtopicName)) {
      newSet.delete(subtopicName);
    } else {
      newSet.add(subtopicName);
    }
    setExpandedSubtopics(newSet);
  };

  const renderPrerequisiteNode = (prereq: any, idx: number, level: number = 0) => {
    if (typeof prereq === 'string') {
      return (
        <Box key={idx} sx={{
          background: '#ede9fe',
          borderRadius: '8px',
          padding: '8px 12px',
          marginLeft: level * 20,
          marginBottom: '4px',
          borderLeft: '3px solid #8b5cf6',
          fontSize: '13px',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Psychology sx={{ fontSize: 14, color: '#5b21b6' }} />
            <Typography variant="body2" sx={{ color: '#5b21b6' }}>{prereq}</Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box key={idx} sx={{ mb: 1 }}>
        <Box sx={{
          background: '#ede9fe',
          borderRadius: '8px',
          padding: '8px 12px',
          marginLeft: level * 20,
          marginBottom: '4px',
          borderLeft: '3px solid #8b5cf6',
          fontSize: '13px',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Psychology sx={{ fontSize: 14, color: '#5b21b6' }} />
            <Typography variant="body2" sx={{ fontWeight: level === 0 ? 600 : 400, color: '#5b21b6' }}>
              {prereq.name}
            </Typography>
          </Box>
        </Box>
        {prereq.children && prereq.children.length > 0 && (
          <Box sx={{ ml: 2 }}>
            {prereq.children.map((child: any, childIndex: number) =>
              renderPrerequisiteNode(child, childIndex, level + 1)
            )}
          </Box>
        )}
      </Box>
    );
  };

  const handleItemClick = (type: 'topic' | 'subtopic', name: string, data: any) => {
    onNodeClick(type, name, data);
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        overflowY: 'auto',
        height: '100%',
        bgcolor: '#f8fafc',
      }}
    >
      <Paper sx={{ maxWidth: 1000, mx: 'auto', borderRadius: 3, overflow: 'hidden' }}>
        <Box
          sx={{
            p: 2,
            bgcolor: '#4f46e5',
            color: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {selectedNode === 'subject' && data.subject}
            {selectedNode === 'topic' && `${selectedTopic} - Performance Report`}
            {selectedNode === 'subtopic' && `${selectedSubtopic} - Details`}
          </Typography>
          <Chip
            label={`Class ${data.class}`}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }}
          />
        </Box>

        {filteredData.topics.map((topic, idx) => {
          const totalSubtopics = topic.subtopics?.length || 0;
          const masteredCount = topic.subtopics?.filter(
            (st) => st.best_score !== null && st.best_score >= 75
          ).length || 0;
          const isExpanded = expandedTopics.has(topic.topic);
          const isTopicSelected = selectedNode === 'topic' && selectedTopic === topic.topic;

          return (
            <Box key={idx}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: isTopicSelected ? '#f0fdf4' : (isExpanded ? '#f0f4ff' : '#fff'),
                  borderBottom: '1px solid #e2e8f0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s ease',
                  borderLeft: `4px solid ${isTopicSelected ? '#10b981' : 'transparent'}`,
                  '&:hover': {
                    bgcolor: '#f8faff',
                  },
                }}
                onClick={() => {
                  toggleTopic(topic.topic);
                  handleItemClick('topic', topic.topic, topic);
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: isTopicSelected ? '#10b981' : '#4f46e5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 13,
                    }}
                  >
                    {idx + 1}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      {topic.topic}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      {masteredCount}/{totalSubtopics} mastered
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={`${Math.round((masteredCount / totalSubtopics) * 100)}%`}
                    size="small"
                    sx={{ bgcolor: '#4f46e5', color: '#fff', minWidth: 45 }}
                  />
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </Box>
              </Box>

              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ bgcolor: '#f8fafc' }}>
                  {topic.subtopics?.map((st, stIdx) => {
                    const score = st.best_score;
                    const color = getPerformanceColor(score);
                    const isSubtopicSelected = selectedNode === 'subtopic' && selectedSubtopic === st.subtopic;
                    const isSubtopicExpanded = expandedSubtopics.has(st.subtopic);
                    const hasPrerequisites = st.prerequisites && st.prerequisites.length > 0;
                    const conn = findConnectedNodes(data, st.subtopic);

                    return (
                      <Box key={stIdx}>
                        <Box
                          sx={{
                            p: 1.5,
                            pl: { xs: 4, sm: 8 },
                            borderBottom: '1px solid #e2e8f0',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            transition: 'all 0.2s ease',
                            bgcolor: isSubtopicSelected ? `${color}22` : 'transparent',
                            borderLeft: `4px solid ${isSubtopicSelected ? color : 'transparent'}`,
                            '&:hover': {
                              bgcolor: '#f0f4ff',
                            },
                          }}
                          onClick={() => {
                            if (hasPrerequisites) {
                              toggleSubtopic(st.subtopic);
                            }
                            handleItemClick('subtopic', st.subtopic, st);
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                bgcolor: color,
                                opacity: 0.6,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: 10,
                                fontWeight: 600,
                              }}
                            >
                              {stIdx + 1}
                            </Box>
                            <Box>
                              <Typography variant="body2">{st.subtopic}</Typography>
                              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                Tests: {st.tests_taken || 0} | Progress: {st.learned_percentage || 0}%
                              </Typography>
                              {st.learning_status && (
                                <Chip
                                  label={st.learning_status}
                                  size="small"
                                  sx={{
                                    mt: 0.5,
                                    bgcolor: st.learning_status === 'mastered' ? '#d1fae5' :
                                             st.learning_status === 'in-progress' ? '#fef3c7' :
                                             st.learning_status === 'not-started' ? '#fee2e2' : '#e5e7eb',
                                    color: st.learning_status === 'mastered' ? '#065f46' :
                                           st.learning_status === 'in-progress' ? '#92400e' :
                                           st.learning_status === 'not-started' ? '#991b1b' : '#374151',
                                    fontWeight: 600,
                                    fontSize: '10px',
                                  }}
                                />
                              )}
                              {st.prerequisites && st.prerequisites.length > 0 && (
                                <Typography variant="caption" sx={{ display: 'block', color: '#5b21b6', mt: 0.5 }}>
                                  📚 Prerequisites: {st.prerequisites.map((p: any) => typeof p === 'string' ? p : p?.name || '').join(', ')}
                                </Typography>
                              )}
                              {(conn.prerequisites.length > 0 || conn.dependents.length > 0) && (
                                <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 0.5 }}>
                                  {conn.prerequisites.length > 0 && `📚 Before: ${conn.prerequisites.join(', ')}`}
                                  {conn.prerequisites.length > 0 && conn.dependents.length > 0 && ' | '}
                                  {conn.dependents.length > 0 && `🎯 After: ${conn.dependents.join(', ')}`}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {st.trend && (
                              st.trend === 'improving' ? (
                                <TrendingUp sx={{ color: '#10b981', fontSize: 18 }} />
                              ) : st.trend === 'declining' ? (
                                <TrendingDown sx={{ color: '#ef4444', fontSize: 18 }} />
                              ) : null
                            )}
                            {score !== null && (
                              <Chip
                                label={`${score}%`}
                                size="small"
                                sx={{
                                  bgcolor: color,
                                  color: '#fff',
                                  fontWeight: 600,
                                  minWidth: 45,
                                }}
                              />
                            )}
                            {hasPrerequisites && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSubtopic(st.subtopic);
                                }}
                              >
                                {isSubtopicExpanded ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            )}
                            <Chip
                              label="View"
                              size="small"
                              variant="outlined"
                              sx={{ borderColor: '#4f46e5', color: '#4f46e5' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleItemClick('subtopic', st.subtopic, st);
                              }}
                            />
                          </Box>
                        </Box>

                        {hasPrerequisites && (
                          <Collapse in={isSubtopicExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ pl: { xs: 6, sm: 10 }, pr: 2, pb: 1.5, pt: 1, bgcolor: '#fafbfc' }}>
                              <Typography variant="caption" sx={{ color: '#5b21b6', fontWeight: 600, display: 'block', mb: 1 }}>
                                📚 Prerequisites (Before):
                              </Typography>
                              {st.prerequisites.map((prereq, prereqIdx) =>
                                renderPrerequisiteNode(prereq, prereqIdx)
                              )}
                            </Box>
                          </Collapse>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};